import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, ChevronRight, Star } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_OPTIONS = ["ALL", "DRAFT", "PUBLISHED", "EXPIRED"];
const TYPE_OPTIONS = ["ALL", "EVENT", "GAME", "PARTNER_UPDATE", "COMMUNITY_BRIEF", "EXPERT_TIP"];

const statusStyle = (s) => {
  if (s === "PUBLISHED") return "text-lv-active bg-lv-active/10";
  if (s === "EXPIRED") return "text-lv-inactive bg-lv-inactive/10";
  return "text-amber-400 bg-amber-400/10";
};

const typeLabel = (t) => t.replace(/_/g, " ");

export default function ContentList() {
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");

  const params = new URLSearchParams();
  if (statusFilter !== "ALL") params.set("status", statusFilter);
  if (typeFilter !== "ALL") params.set("content_type", typeFilter);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["admin-content", statusFilter, typeFilter],
    queryFn: () => api.get(`/admin/content?${params.toString()}`),
  });

  if (isLoading) return <div className="py-12 text-center"><div className="w-10 h-0.5 bg-bronze mx-auto animate-pulse" /></div>;

  return (
    <div data-testid="content-list-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-cream tracking-wide">Published Content</h1>
          <p className="text-sm text-lv-muted tracking-wide mt-1">{items.length} item{items.length !== 1 ? "s" : ""}</p>
        </div>
        <Link to="/admin/content/new" className="lv-btn-primary inline-flex items-center gap-2 min-h-[44px]" data-testid="create-content-btn">
          <Plus size={16} /> New Content
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[160px] bg-walnut-deep border-lv-border text-cream min-h-[44px]" data-testid="content-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-walnut-deep border-lv-border">
            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-[200px] bg-walnut-deep border-lv-border text-cream min-h-[44px]" data-testid="content-type-filter">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-walnut-deep border-lv-border">
            {TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{typeLabel(t)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {items.map((item) => (
          <Link
            key={item.id}
            to={`/admin/content/${item.id}`}
            className="block bg-walnut border border-lv-border p-4 sm:p-5 hover:border-bronze/40 transition-colors group"
            data-testid={`content-row-${item.id}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <h3 className="text-sm text-cream font-medium tracking-wide">{item.title}</h3>
                  <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 ${statusStyle(item.status)}`}>{item.status}</span>
                  <span className="text-[9px] uppercase tracking-wider text-lv-muted/60 border border-lv-border/40 px-2 py-0.5">{typeLabel(item.content_type)}</span>
                  {item.is_featured && <Star size={12} className="text-bronze fill-bronze" />}
                </div>
                <div className="flex items-center gap-4 text-xs text-lv-muted">
                  {item.source_event_title && <span>Event: {item.source_event_title}</span>}
                  {item.publish_date && <span>Pub: {item.publish_date}</span>}
                  {item.expiration_date && <span>Exp: {item.expiration_date}</span>}
                </div>
              </div>
              <ChevronRight size={16} className="text-lv-muted group-hover:text-bronze transition-colors shrink-0" />
            </div>
          </Link>
        ))}
        {items.length === 0 && <p className="text-sm text-lv-muted text-center py-8">No content found.</p>}
      </div>
    </div>
  );
}
